/**
 * Trail-eligibility predicate — does a sequence of recent positions
 * represent a "moving Producer" that warrants a polyline trail?
 *
 * Stream A PR 2 / commit M5 — REQ-UI-012's threshold gate. ADR-016
 * Open Item 4 default (accepted 2026-05-12 prereqs PR): **50 metres
 * within the trail window**. Below threshold the Producer is
 * treated as stationary (sensor jitter or stationary platform) and
 * no trail is rendered — avoids cluttering the map with thousand
 * stationary platforms' GPS jitter.
 *
 * # The check: maximum pairwise haversine distance
 *
 * Trail points are a time-ascending sequence of WGS84 lat/lon
 * pairs. The Producer is considered moving when the **maximum
 * great-circle distance between any two points in the sequence**
 * exceeds the threshold.
 *
 * Why max-pairwise (vs. bounding-box, vs. start-to-end)?
 *
 *   - **Bounding-box** would underestimate diagonal movement
 *     because the box's diagonal is ≤ √2 × the max-pair distance
 *     when the trail is roughly NE/SW oriented; ≥ for E/W or N/S
 *     trails. The bias direction depends on orientation, which is
 *     operationally weird.
 *   - **Start-to-end** would miss a Producer that traveled out and
 *     came back (e.g., a patrol that returned to base). The
 *     storyboard "moving Producer" intent is about visible motion
 *     in the window, not net displacement.
 *   - **Max-pairwise haversine** captures "did the Producer's
 *     position vary by more than threshold at any point during the
 *     window?" — which is the operator-facing question REQ-UI-012
 *     is asking.
 *
 * Pairwise is O(N²) where N is trail-point count. For the MVP
 * scale (15-minute window, ~1 Hz status rate per Open Item 6 = up
 * to 900 points; typical ~60), an O(N²) loop on the client is well
 * under 1 ms. If the trail-point count balloons past the MVP
 * design we'd switch to convex-hull / pruning — flagged here as
 * a future-perf note rather than implemented preemptively.
 *
 * # Haversine — the formula
 *
 * Standard great-circle distance:
 *
 *   a = sin²(Δφ/2) + cos φ₁ · cos φ₂ · sin²(Δλ/2)
 *   c = 2 · atan2(√a, √(1−a))
 *   d = R · c
 *
 * where ``R = 6_371_000`` metres (WGS84-compatible mean Earth
 * radius). Accurate to ~0.5% globally — well within the operator
 * tolerance for a 50 m threshold check.
 *
 * # Threshold = 0 edge case
 *
 * REQ-UI-012's threshold is a **strict less-than** boundary: at
 * exactly 50 m the Producer is **not** moving per the requirement
 * wording "*more than* the configured threshold". The predicate
 * uses ``>``, matching the requirement.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering for moving Producers).
 *   - ADR-016 §5 (trail-view scope).
 *   - ADR-016 Open Item 4 (50 m threshold default — accepted
 *     2026-05-12).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */

/**
 * Default trail-eligibility threshold — ADR-016 Open Item 4
 * MVP default, accepted 2026-05-12 in the prereqs PR.
 *
 * 50 m balances:
 *   - GPS jitter / sensor noise (typically < 10 m on stationary
 *     consumer-grade GPS; military / surveyed platforms tighter).
 *   - Operationally meaningful displacement (a foot patrol or
 *     vehicle covers > 50 m well within a 15-minute window;
 *     a stationary radar or camera does not).
 *
 * Configurable per operator session post-MVP per ADR-016 §5; the
 * UI-side hook that consumes :func:`isTrailEligible` will read the
 * preference once that surface exists. M5 ships the constant.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - ADR-016 Open Item 4.
 */
export const TRAIL_ELIGIBILITY_THRESHOLD_M = 50;

/**
 * Mean Earth radius in metres — the WGS84-compatible value used by
 * :func:`haversineDistanceM`. Pulled out as a named constant so
 * tests can reach it for cross-checks against published reference
 * implementations.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export const EARTH_RADIUS_M = 6_371_000;

/**
 * A single trail point: minimum surface a position-only predicate
 * needs — lat / lon in decimal degrees. Wider :type:`TrailItem`
 * records carry status / battery / etc., but the eligibility check
 * only cares about geometry. Accepting a narrower shape keeps
 * :func:`isTrailEligible` test-friendly (small literals over
 * mock-StatusItem fixtures).
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export interface TrailPoint {
  /** WGS84 latitude in decimal degrees. */
  latitude_deg: number;
  /** WGS84 longitude in decimal degrees. */
  longitude_deg: number;
}

/**
 * Convert decimal degrees to radians.
 *
 * Exported for test reach — :file:`trail-eligibility.test.ts` uses
 * the helper to sanity-check the haversine formula against known
 * inputs without re-deriving the conversion.
 */
export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle distance between two lat/lon points, in metres.
 *
 * Standard haversine formula (see module docstring §"Haversine —
 * the formula" for the derivation). Exported for testability + so
 * future callers (per-leg trail-distance UI, distance-to-nearest-
 * neighbour clustering refinement) can reuse without duplicating.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function haversineDistanceM(a: TrailPoint, b: TrailPoint): number {
  const phi1 = toRadians(a.latitude_deg);
  const phi2 = toRadians(b.latitude_deg);
  const deltaPhi = toRadians(b.latitude_deg - a.latitude_deg);
  const deltaLambda = toRadians(b.longitude_deg - a.longitude_deg);

  const sinDPhi = Math.sin(deltaPhi / 2);
  const sinDLambda = Math.sin(deltaLambda / 2);

  const aa = sinDPhi * sinDPhi + Math.cos(phi1) * Math.cos(phi2) * sinDLambda * sinDLambda;

  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return EARTH_RADIUS_M * c;
}

/**
 * Maximum pairwise great-circle distance across a sequence of trail
 * points.
 *
 * Returns 0 for an empty or single-point sequence (no movement
 * possible). O(N²); see module docstring for the performance note.
 *
 * Exported as a primitive so tests can probe the distance directly
 * and the hook can show "max excursion" telemetry post-MVP without
 * re-deriving.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export function maxPairwiseDistanceM(points: readonly TrailPoint[]): number {
  if (points.length < 2) {
    return 0;
  }
  let maxDistance = 0;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      // ``!`` is justified: both indices are guaranteed valid by the
      // loop bounds; TS's array-access strictness can't see that.
      const a = points[i]!;
      const b = points[j]!;
      const d = haversineDistanceM(a, b);
      if (d > maxDistance) {
        maxDistance = d;
      }
    }
  }
  return maxDistance;
}

/**
 * Is this trail eligible for rendering as a polyline?
 *
 * The predicate REQ-UI-012 specifies: position changed by **more
 * than** ``threshold_m`` (default :const:`TRAIL_ELIGIBILITY_THRESHOLD_M`
 * = 50 m) across the sequence.
 *
 * Empty or single-point trails return ``false`` (cannot be moving
 * with < 2 points).
 *
 * @param points - Time-ordered trail points (order doesn't matter
 *     for this predicate; max-pairwise is order-agnostic).
 * @param thresholdM - Override the default threshold (used by
 *     tests + by the future operator-preference plumbing).
 * @returns ``true`` if any two points are more than ``thresholdM``
 *     apart by great-circle distance.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - ADR-016 §5 / Open Item 4.
 */
export function isTrailEligible(
  points: readonly TrailPoint[],
  thresholdM: number = TRAIL_ELIGIBILITY_THRESHOLD_M,
): boolean {
  return maxPairwiseDistanceM(points) > thresholdM;
}
